import React, { useEffect, useState, useCallback } from "react";
import { Box } from "@mui/material";
import Chatbox from "./Chatbox"; // Import the new Chatbox component
import axios from "axios"; // Import axios for API calls
import { useUser } from "../UserContext"; // Import the useUser hook

// Updated MessageData interface to match new schema
export interface MessageData {
  id: number;
  context_id: number;
  sender_id: number;
  sender_selected_role_id: number;
  created_at: string;
  updated_at: string;
  sender_avatar_url: string;
  message: string;
  attachment_url: string | null;
  context: string;
  status: string;
  msg_type: string;
  sender_name: string;
  sender_role: string;
  selected_role_details: string;
}

interface MessagesProps {
  contextId: number;
  context?: string; // Make optional with default "Event"
}

const Messages: React.FC<MessagesProps> = ({
  contextId,
  context = "Event",
}) => {
  const { currentUser, selectedRoleId } = useUser();
  const [messages, setMessages] = useState<MessageData[]>([]);
  const [loadingMessages, setLoadingMessages] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [socket, setSocket] = useState<WebSocket | null>(null);
  const [usePolling, setUsePolling] = useState(false);

  // Initialize WebSocket connection with updated path
  useEffect(() => {
    const checkWebSocketSupport = async () => {
      try {
        const response = await axios.get(
          `${import.meta.env.VITE_API_URL}/chat/ws-test`
        );

        if (!response.data.wsEnabled) {
          setUsePolling(true);
          return;
        }

        const ws = new WebSocket(
          `wss://api.promoraven.com/chat/ws?context=${context}&contextId=${contextId}`
        );

        ws.onopen = () => {
          setUsePolling(false);
        };

        ws.onclose = () => {
          setError("WebSocket connection closed");
          setUsePolling(true);
        };

        ws.onmessage = (event) => {
          const data = JSON.parse(event.data);
          if (data.type === "new_message") {
            setMessages((prev) => [...prev, data.message]);
          } else if (data.type === "message_updated") {
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === data.message.id ? data.message : msg
              )
            );
          }
        };

        ws.onerror = () => {
          setUsePolling(true);
        };

        setSocket(ws);

        return () => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.close();
          }
        };
      } catch (error) {
        setUsePolling(true);
      }
    };

    checkWebSocketSupport();
  }, [contextId, context]);

  // Polling effect
  useEffect(() => {
    if (!usePolling) return;

    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(
          `${
            import.meta.env.VITE_API_URL
          }/chat/messages?context=${context}&contextId=${contextId}`
        );
        setMessages(response.data);
      } catch (err) {
        console.error("Polling failed:", err);
      }
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(pollInterval);
  }, [usePolling, context, contextId]);

  // Fetch messages using new criteria
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        setLoadingMessages(true);
        const response = await axios.get(
          `${
            import.meta.env.VITE_API_URL
          }/chat/messages?context=${context}&contextId=${contextId}`
        );
        setMessages(response.data);
        console.log(response.data);
        setError(null);
      } catch (err) {
        setError("Failed to load messages.");
        console.error(err);
      } finally {
        setLoadingMessages(false);
      }
    };

    if (contextId) {
      fetchMessages();
    }
  }, [contextId, context]);

  const handleSendMessage = useCallback(
    async (messageContent: string, attachmentUrl: string | null) => {
      if (!messageContent.trim() || !contextId) return;

      const newMessage = {
        context_id: contextId,
        sender_id: currentUser?.user_id ?? 0,
        sender_selected_role_id: selectedRoleId ?? 0,
        message: messageContent,
        attachment_url: attachmentUrl,
        context: context,
        status: "active",
        sender_role: currentUser?.role ?? "user",
        msg_type: "direct",
      };
      console.log(newMessage);

      try {
        if (socket?.readyState === WebSocket.OPEN) {
          socket.send(
            JSON.stringify({
              type: "new_message",
              message: newMessage,
            })
          );
          console.log("Message sent to WebSocket");

          // Add fallback GET request after short delay to ensure message was received
          setTimeout(async () => {
            try {
              const response = await axios.get(
                `${
                  import.meta.env.VITE_API_URL
                }/chat/messages?context=${context}&contextId=${contextId}`
              );
              setMessages(response.data);
            } catch (err) {
              console.error("Fallback GET request failed:", err);
            }
          }, 1000); // 1 second delay
        } else {
          const response = await axios.post(
            `${import.meta.env.VITE_API_URL}/chat/messages`,
            newMessage
          );
          console.log("Message sent via Http Request");
          if (response.data) {
            setMessages((prev) => [...prev, response.data]);
          }
        }
      } catch (error) {
        console.error("Error sending message:", error);
        setError("Failed to send message");

        // Fallback GET request if any error occurs
        try {
          const response = await axios.get(
            `${
              import.meta.env.VITE_API_URL
            }/chat/messages?context=${context}&contextId=${contextId}`
          );
          setMessages(response.data);
        } catch (err) {
          console.error("Fallback GET request failed:", err);
        }
      }
    },
    [socket, contextId, currentUser, selectedRoleId, context]
  );

  //FormatDateForSQL

  const handleEditMessage = async (
    IdToEdit: number | null,
    DateToEdit: string | null,
    newMessageContent: string
  ) => {
    try {
      let valueToSend;
      let isDate;

      if (IdToEdit !== null) {
        valueToSend = IdToEdit; // Use ID for editing
        isDate = false; // Set isDate to false since we're using ID
      } else if (IdToEdit === null) {
        valueToSend = DateToEdit; // Use date for editing
        isDate = true; // Set isDate to true since we're using date
      } else {
        throw new Error("No valid ID or Date provided for editing.");
      }

      await axios.put(
        `${import.meta.env.VITE_API_URL}/chat/messages/edit/${valueToSend}`,
        {
          message: newMessageContent,
          isDate: isDate, // Include isDate in the request body
        }
      ); // Send the determined value

      setMessages((prevMessages) => {
        return prevMessages.map((msg) =>
          (isDate ? msg.created_at === DateToEdit : msg.id === IdToEdit)
            ? { ...msg, message: newMessageContent }
            : msg
        );
      }); // Update the message in the state
    } catch (error) {
      console.error("Error editing message:", error);
    }
  };

  const handleDeleteMessage = async (
    IdToDelete: number | null,
    DateToDelete: string | null
  ) => {
    try {
      let valueToSend;
      let isDate;

      if (IdToDelete !== null) {
        valueToSend = IdToDelete; // Use ID for deletion
        isDate = false; // Set isDate to false since we're using ID
      } else if (DateToDelete) {
        valueToSend = DateToDelete; // Use date for deletion
        isDate = true; // Set isDate to true since we're using date
      } else {
        throw new Error("No valid ID or Date provided for deletion.");
      }

      await axios.put(
        `${import.meta.env.VITE_API_URL}/chat/messages/delete/${valueToSend}`,
        {
          value: valueToSend,
          isDate: isDate, // Include isDate in the request body
        }
      ); // Send the determined value

      setMessages((prevMessages) => {
        if (isDate) {
          // If DateToDelete is provided, filter based on created_at
          return prevMessages.filter((msg) => msg.created_at !== DateToDelete);
        } else {
          // If IdToDelete is provided, filter based on id
          return prevMessages.filter((msg) => msg.id !== IdToDelete);
        }
      }); // Filter out the deleted message
    } catch (error) {
      console.error("Error deleting message:", error);
    }
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Chatbox
        selectedThreadId={contextId}
        currentUserId={currentUser?.user_id ?? 0}
        messages={messages}
        onSendMessage={handleSendMessage}
        onEditMessage={handleEditMessage}
        onDeleteMessage={handleDeleteMessage}
        loading={loadingMessages}
        error={error}
      />
    </Box>
  );
};

export default Messages;
